import React, { useEffect, useRef, useState } from 'react';
import { Code, Cloud, Link2, GraduationCap, Headphones, Lightbulb } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback.tsx';
import './OurExpertise.css';

const expertiseAreas = [
  {
    icon: Code,
    title: 'Custom Development',
    description: 'Tailor-made software solutions designed to meet your unique business requirements.',
    imageUrl: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwdGVhbXxlbnwxfHx8fDE3NjQyODc4Njh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['Web Applications', 'Mobile Apps', 'Enterprise Software'],
  },
  {
    icon: Cloud,
    title: 'Cloud Solutions',
    description: 'Scalable cloud infrastructure and migration services for modern businesses.',
    imageUrl: 'https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMGNvbXB1dGluZyUyMHNlcnZlcnN8ZW58MXx8fHwxNzY0MjYyMjQ3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['Cloud Migration', 'DevOps', 'Infrastructure'],
  },
  {
    icon: Link2,
    title: 'Integrations',
    description: 'Seamless system integrations to unify your business operations and data.',
    imageUrl: 'https://images.unsplash.com/photo-1650978810641-6610f4b6d15a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGludGVncmF0aW9ufGVufDF8fHx8MTc2NDM2Mjc5OHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['API Integration', 'Automation', 'Data Sync'],
  },
  {
    icon: GraduationCap,
    title: 'Corporate Training',
    description: 'Comprehensive training programs to upskill your workforce on latest technologies.',
    imageUrl: 'https://images.unsplash.com/photo-1762176263996-a0713a49ee4d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHRlY2hub2xvZ3l8ZW58MXx8fHwxNzY0MzYyNzk5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['Custom Workshops', 'Certifications', 'Mentorship'],
  },
  {
    icon: Headphones,
    title: 'Managed Support',
    description: '24/7 technical support and maintenance to keep your systems running smoothly.',
    imageUrl: 'https://images.unsplash.com/photo-1643981509466-c37ac9d10045?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwc3VwcG9ydCUyMG1hbmFnZWR8ZW58MXx8fHwxNzY0MzYyNzk5fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['24/7 Monitoring', 'Incident Response', 'Optimization'],
  },
  {
    icon: Lightbulb,
    title: 'Consulting',
    description: 'Strategic technology consulting to guide your digital transformation journey.',
    imageUrl: 'https://images.unsplash.com/photo-1758691736084-4ef3e6f6a2cf?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBzdHJhdGVneXxlbnwxfHx8fDE3NjQyOTUwMjh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    highlights: ['Strategy Planning', 'Tech Assessment', 'Roadmapping'],
  },
];

export function OurExpertise() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index]);
            }
          }
        });
      },
      { threshold: 0.15 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, [visibleCards]);

  return (
    <section className="our-expertise section">
      <div className="expertise-header">
        <h2 className="section-title">Our Expertise</h2>
        <p className="section-subtitle">
          Comprehensive technology services to power your business forward.
        </p>
      </div>

      <div className="expertise-grid">
        {expertiseAreas.map((area, index) => {
          const Icon = area.icon;
          return (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`expertise-card ${visibleCards.includes(index) ? 'visible' : ''}`}
              style={{ animationDelay: `${(index % 3) * 0.1}s` }}
            >
              <div className="expertise-image-container">
                <ImageWithFallback
                  src={area.imageUrl}
                  alt={area.title}
                  className="expertise-image"
                />
                <div className="expertise-image-overlay"></div>
                <div className="expertise-icon-overlay">
                  <Icon size={36} />
                </div>
              </div>

              <div className="expertise-content">
                <h3 className="expertise-title">{area.title}</h3>
                <p className="expertise-description">{area.description}</p>

                <div className="expertise-highlights">
                  {area.highlights.map((highlight, idx) => (
                    <span key={idx} className="highlight-badge">
                      {highlight}
                    </span>
                  ))}
                </div>

                <button className="expertise-btn">
                  Explore
                  <span className="btn-arrow-expertise">→</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
